import { useEffect, useRef, useState } from "react";

/**
 * Controls drawer open state for the management panel.
 *
 * - Auto-opens once when the queue transitions from 0 → 1+ items
 *   (gives immediate feedback that an item was queued).
 *   Subsequent additions don't reopen if the user has closed it.
 * - ESC key closes the drawer when open.
 */
export function useManagementDrawer(queuedCount: number) {
  const [open, setOpen] = useState(false);
  const prevCountRef = useRef(0);

  // Auto-open on first queue transition
  useEffect(() => {
    if (prevCountRef.current === 0 && queuedCount > 0) {
      setOpen(true);
    }
    prevCountRef.current = queuedCount;
  }, [queuedCount]);

  // ESC closes
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open]);

  return [open, setOpen] as const;
}
